import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';
import Toast, { ToastType, ToastAction } from '../components/common/Toast';

type ToastPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

interface ToastData {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  position?: ToastPosition;
  action?: ToastAction;
  dismissible?: boolean;
  showProgress?: boolean;
  icon?: React.ReactNode;
}

interface PromiseToastMessages {
  loading: { title: string; message?: string };
  success: { title: string; message?: string };
  error: { title: string; message?: string };
}

interface ToastContextType {
  showToast: (toast: Omit<ToastData, 'id'>) => string;
  showSuccess: (title: string, message?: string, duration?: number, position?: ToastPosition) => string;
  showError: (title: string, message?: string, duration?: number, position?: ToastPosition) => string;
  showWarning: (title: string, message?: string, duration?: number, position?: ToastPosition) => string;
  showInfo: (title: string, message?: string, duration?: number, position?: ToastPosition) => string;
  promise: <T>(promise: Promise<T>, messages: PromiseToastMessages, position?: ToastPosition) => Promise<T>;
  removeToast: (id: string) => void;
  updateToast: (id: string, updates: Partial<Omit<ToastData, 'id'>>) => void;
  clearAllToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const generateId = () => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const showToast = useCallback((toast: Omit<ToastData, 'id'>): string => {
    const id = generateId();
    const newToast: ToastData = {
      id,
      duration: 5000, // Default 5 seconds
      position: 'top-right', // Default position
      dismissible: true,
      showProgress: true,
      ...toast,
    };

    setToasts(prev => [...prev, newToast]);
    return id;
  }, []);

  const showSuccess = useCallback((title: string, message?: string, duration?: number, position: ToastPosition = 'top-right'): string => {
    return showToast({ type: 'success', title, message, duration, position });
  }, [showToast]);

  const showError = useCallback((title: string, message?: string, duration?: number, position: ToastPosition = 'top-right'): string => {
    return showToast({ type: 'error', title, message, duration: duration || 7000, position }); // Errors stay longer
  }, [showToast]);

  const showWarning = useCallback((title: string, message?: string, duration?: number, position: ToastPosition = 'bottom-center'): string => {
    return showToast({ type: 'warning', title, message, duration, position });
  }, [showToast]);

  const showInfo = useCallback((title: string, message?: string, duration?: number, position: ToastPosition = 'bottom-center'): string => {
    return showToast({ type: 'info', title, message, duration, position });
  }, [showToast]);

  const updateToast = useCallback((id: string, updates: Partial<Omit<ToastData, 'id'>>) => {
    setToasts(prev => prev.map(toast =>
      toast.id === id ? { ...toast, ...updates } : toast
    ));
  }, []);

  const promise = useCallback(async <T,>(
    promiseToResolve: Promise<T>,
    messages: PromiseToastMessages,
    position: ToastPosition = 'top-right'
  ): Promise<T> => {
    const toastId = showToast({
      type: 'info',
      title: messages.loading.title,
      message: messages.loading.message,
      duration: 0, // Loading toast stays until promise resolves
      dismissible: false,
      showProgress: false,
      position,
    });

    try {
      const result = await promiseToResolve;
      updateToast(toastId, {
        type: 'success',
        title: messages.success.title,
        message: messages.success.message,
        duration: 5000,
        dismissible: true,
        showProgress: true,
      });
      return result;
    } catch (error) {
      updateToast(toastId, {
        type: 'error',
        title: messages.error.title,
        message: messages.error.message,
        duration: 7000,
        dismissible: true,
        showProgress: true,
      });
      throw error;
    }
  }, [showToast, updateToast]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const value = useMemo<ToastContextType>(() => ({
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    promise,
    removeToast,
    updateToast,
    clearAllToasts
  }), [showToast, showSuccess, showError, showWarning, showInfo, promise, removeToast, updateToast, clearAllToasts]);

  const renderToast = (toast: ToastData) => (
    <Toast
      key={toast.id}
      id={toast.id}
      type={toast.type}
      title={toast.title}
      message={toast.message}
      duration={toast.duration}
      action={toast.action}
      dismissible={toast.dismissible}
      showProgress={toast.showProgress}
      icon={toast.icon}
      onClose={removeToast}
    />
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      
      {/* Toast Containers - All 6 positions */}
      {/* Top-left */}
      <div className="fixed top-4 left-4 z-[10000] space-y-2 pointer-events-none">
        {toasts.filter(toast => toast.position === 'top-left').map(renderToast)}
      </div>
      
      {/* Top-center */}
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[10000] space-y-2 pointer-events-none">
        {toasts.filter(toast => toast.position === 'top-center').map(renderToast)}
      </div>
      
      {/* Top-right */}
      <div className="fixed top-4 right-4 z-[10000] space-y-2 pointer-events-none">
        {toasts.filter(toast => toast.position === 'top-right').map(renderToast)}
      </div>
      
      {/* Bottom-left */}
      <div className="fixed bottom-4 left-4 z-[10000] space-y-2 pointer-events-none">
        {toasts.filter(toast => toast.position === 'bottom-left').map(renderToast)}
      </div>
      
      {/* Bottom-center */}
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-[10000] space-y-2 pointer-events-none">
        {toasts.filter(toast => toast.position === 'bottom-center').map(renderToast)}
      </div>
      
      {/* Bottom-right */}
      <div className="fixed bottom-4 right-4 z-[10000] space-y-2 pointer-events-none">
        {toasts.filter(toast => toast.position === 'bottom-right').map(renderToast)}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
